import * as C from '../../constants';
import SkiSchoolService from '../../../../services/ski/ski-school-service';
import BookingSkiSchoolService from '../../../../services/ski/booking-ski-school-service';
import exceptionValidator from '../../../../utils/exceptionValidator';

export default {

  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.SKI_SCHOOL_RESET_STATE);
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  availableSchoolPricesLoad({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_AVAILABLE_PRODUCTS_LOAD_PENDING);

    return SkiSchoolService.searchAvailableSkiSchoolPrices(payload)
      .then((res) => {
        commit(C.SKI_SCHOOL_AVAILABLE_PRODUCTS_LOAD_SUCCESS, res.data);
        if (res.data.length) {
          dispatch('loadSkiSchoolAreaPickupsBySeasonId', res.data[0].season.id);
        }
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_AVAILABLE_PRODUCTS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param bookingId
   * @returns {Promise<void>|Promise<T>}
   */
  schoolsByBookingIdLoad({ commit }, bookingId) {
    commit(C.SKI_SCHOOL_PRODUCTS_LOAD_PENDING);

    return BookingSkiSchoolService.getSkiSchoolByBookingId(bookingId)
      .then((res) => {
        commit(C.SKI_SCHOOL_PRODUCTS_LOAD_SUCCESS, res.data ? res.data : []);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PRODUCTS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolCreate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_CREATE_PENDING);
    return BookingSkiSchoolService.createSkiSchool(
      payload.bookingParticipantId,
      { ...payload.skiSchool, bookingId: payload.bookingId },
    ).then(() => {
      commit(C.SKI_SCHOOL_CREATE_SUCCESS);
      dispatch('schoolsByBookingIdLoad', payload.bookingId);
    }).catch((error) => {
      const errorMessages = exceptionValidator.parseErrorException(error, true);
      commit(C.SKI_SCHOOL_CREATE_FAILURE, errorMessages);
    });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @returns {Promise<T>}
   */
  schoolSupplierRefUpdate({ commit, state }, payload) {
    commit(C.SKI_SCHOOL_SUPPLIER_REF_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchoolWithSupplierRef(
      payload.skiSchool.id,
      payload.supplierRef,
    )
      .then(() => {
        const currentSkiSchool = {
          ...Object.assign({}, payload.skiSchool),
          supplierRef: payload.supplierRef,
        };
        const items = Object.values(state.schools.items).map(
          school => (
            school.id === currentSkiSchool.id ? currentSkiSchool : school
          ),
        );
        commit(C.SKI_SCHOOL_SUPPLIER_REF_UPDATE_SUCCESS, items);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_SUPPLIER_REF_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  loadSkiSchoolAreaPickupsBySeasonId({ commit }, seasonId) {
    commit(C.SKI_SCHOOL_PICKUP_AREA_LOAD_PENDING);

    return SkiSchoolService.loadSkiSchoolAreaPickupsBySeasonId(seasonId)
      .then((res) => {
        commit(C.SKI_SCHOOL_PICKUP_AREA_LOAD_SUCCESS, { items: res.data.filter(area => area.selected) });
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PICKUP_AREA_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolMeetingPointUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_PICKUP_AREA_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchoolWithAreaPickup(
      payload.skiSchoolId,
      payload.meetingPointRequest,
    )
      .then(() => {
        commit(C.SKI_SCHOOL_PICKUP_AREA_UPDATE_SUCCESS);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PICKUP_AREA_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolStartDateTimeUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_START_DATE_TIME_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchoolWithStartDateTime(
      payload.skiSchoolId,
      payload.startDateTime,
    )
      .then(() => {
        commit(C.SKI_SCHOOL_START_DATE_TIME_UPDATE_SUCCESS, payload);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);

        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_START_DATE_TIME_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolSupplierCommentUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_SUPPLIER_COMMENT_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchoolWithSupplierComment(
      payload.skiSchoolId,
      payload.comment,
    )
      .then(() => {
        commit(C.SKI_SCHOOL_SUPPLIER_COMMENT_UPDATE_SUCCESS, payload);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);

        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_SUPPLIER_COMMENT_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolCustomerCommentUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_CUSTOMER_COMMENT_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchoolWithCustomerComment(
      payload.skiSchoolId,
      payload.comment,
    )
      .then(() => {
        commit(C.SKI_SCHOOL_CUSTOMER_COMMENT_UPDATE_SUCCESS, payload);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);

        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_CUSTOMER_COMMENT_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolCancel({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_CANCEL_PENDING);

    return BookingSkiSchoolService.cancelSkiSchool(payload.skiSchoolId, payload.salesAgentUserId)
      .then(() => {
        commit(C.SKI_SCHOOL_CANCEL_SUCCESS, payload.skiSchoolId);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        window.location.reload();
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_CANCEL_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  reopenSkiSchool({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_STATUS_PENDING);

    return BookingSkiSchoolService.reopenSkiSchool(payload.skiSchoolId)
      .then(() => {
        commit(C.SKI_SCHOOL_CHANGE_STATUS_SUCCESS, payload.skiSchoolId);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_CHANGE_STATUS_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolStop({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_STOP_PENDING);

    return BookingSkiSchoolService.stopSkiSchool(payload.skiSchoolId, payload.salesAgentUserId)
      .then(() => {
        commit(C.SKI_SCHOOL_STOP_SUCCESS);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        window.location.reload();
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_STOP_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<boolean>}
   */
  schoolUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchool(payload.changedProduct)
      .then(() => {
        commit(C.SKI_SCHOOL_UPDATE_SUCCESS);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<boolean>}
   */
  schoolPriceUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_PRICE_UPDATE_PENDING);

    return BookingSkiSchoolService.updateSkiSchoolPrice(payload.changedProductPrice)
      .then(() => {
        commit(C.SKI_SCHOOL_PRICE_UPDATE_SUCCESS);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_PRICE_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<boolean>}
   */
  schoolCancellationFeeNetUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_CANCELLATION_FEE_NET_UPDATE_PENDING);
    return BookingSkiSchoolService.updateSkiSchoolCancellationFeeNet(payload.skiSchoolId, payload.cancellationFeeNet)
      .then(() => {
        commit(C.SKI_SCHOOL_CANCELLATION_FEE_NET_UPDATE_SUCCESS);
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_SCHOOL_CANCELLATION_FEE_NET_UPDATE_FAILURE, errorMessages);
      });
  },


  /**
   * @param commit
   * @param state
   * @param payload
   * @returns {Promise<T>}
   */
  schoolDelete({ commit, state }, payload) {
    commit(C.SKI_SCHOOL_DELETE_PENDING);

    return BookingSkiSchoolService.deleteSkiSchool(payload.skiSchoolId, payload.salesAgentUserId)
      .then(() => {
        const items = Object.values(state.schools.items)
          .filter(school => school.id !== payload.skiSchoolId);
        commit(C.SKI_SCHOOL_DELETE_SUCCESS, items);
        window.location.reload();
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolParticipantCreate({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_PARTICIPANT_CREATE_PENDING);

    return BookingSkiSchoolService.createSkiSchoolParticipant(
      payload.skiSchoolId,
      payload.bookingParticipantId,
    )
      .then(() => {
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        commit(C.SKI_SCHOOL_PARTICIPANT_CREATE_SUCCESS);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PARTICIPANT_CREATE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  schoolParticipantDelete({ commit, dispatch }, payload) {
    commit(C.SKI_SCHOOL_PARTICIPANT_DELETE_PENDING);

    return BookingSkiSchoolService.deleteSkiSchoolParticipant(
      payload.skiSchoolId,
      payload.bookingParticipantId,
    )
      .then(() => {
        dispatch('schoolsByBookingIdLoad', payload.bookingId);
        commit(C.SKI_SCHOOL_PARTICIPANT_DELETE_SUCCESS);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_PARTICIPANT_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param bookingId
   * @returns {Promise<void>}
   */
  sendSkiSchoolRequestEmail({ commit }, bookingId) {
    commit(C.SKI_SCHOOL_SEND_REQUEST_EMAIL_PENDING);
    return BookingSkiSchoolService.sendSkiSchoolRequestEmail(bookingId)
      .then(() => {
        commit(C.SKI_SCHOOL_SEND_REQUEST_EMAIL_SUCCESS);
      })
      .catch((error) => {
        commit(C.SKI_SCHOOL_SEND_REQUEST_EMAIL_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setBookingParticipant({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_BOOKING_PARTICIPANT_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setNumberOfDays({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_NUMBER_OF_DAYS_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setLessonType({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_LESSON_TYPE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setWeekDayNumber({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_WEEK_DAY_NUMBER_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setLessonDuration({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_LESSON_DURATION_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setNumberOfPersons({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_NUMBER_OF_PERSONS_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setSkiTypes({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_SKI_TYPES_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setArea({ commit }, payload) {
    commit(C.SKI_SCHOOL_SEARCH_AREA_SET, payload);
  },

  /**
   * @param commit
   */
  setClearFilters({ commit }) {
    commit(C.SKI_SCHOOL_SEARCH_CLEAR_FILTERS_SET);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductBookingParticipant({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_BOOKING_PARTICIPANT_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductNumberOfDays({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_NUMBER_OF_DAYS_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductPersonCategory({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_PERSON_CATEGORY_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductArea({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_AREA_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductLessonType({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_LESSON_TYPE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductWeekDayNumber({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_WEEK_DAY_NUMBER_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductLessonDuration({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_LESSON_DURATION_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductLevelType({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_LEVEL_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductNumberOfPersons({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_NUMBER_OF_PERSONS_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductSkiType({ commit }, payload) {
    commit(C.SKI_SCHOOL_CHANGE_PRODUCT_SKI_TYPE_SET, payload);
  },

  bookByXML({ commit }, payload) {
    commit(C.SKI_SCHOOL_BOOK_BY_XML_PENDING);
    return BookingSkiSchoolService.bookByXML(payload).then(() => {
      commit(C.SKI_SCHOOL_BOOK_BY_XML_SUCCESS);
    }).catch((error) => {
      commit(C.SKI_SCHOOL_BOOK_BY_XML_FAILURE, error);
    });
  },

  updateSupplierRefPubnub({ commit, state }, payload) {
    const skiSchools = JSON.parse(JSON.stringify(state.schools.items));
    const skiSchoolIndex = skiSchools.findIndex(school => school.id === payload.id);
    skiSchools[skiSchoolIndex].supplierRef = payload.supplierRef;
    commit(C.SKI_SCHOOL_BOOK_BY_XML_SUCCESS, skiSchools);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/school/actions.js